import { map, Observable, OperatorFunction } from 'rxjs';
import { BaseResponse } from '../models/response.interface';
import { VitalSigns } from '../models/vital-signs.interface';
import { historyDataParameters } from './historical-data-constants';
import { EMFJS, RTFJS, WMFJS } from 'rtf.js';

export function processArrayResponse<
  T extends BaseResponse<R>,
  R
>(): OperatorFunction<T, R> {
  return function (source: Observable<T>): Observable<R> {
    return source.pipe(map((value) => (value.response ?? []) as R));
  };
}

export function processVitalSignsResponse(
  vitalSigns: VitalSigns[],
  callbackFn: (sortedDates: string[]) => void
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
): Record<string, string>[] {
  const displayNameToVitalsMap = new Map<string, Map<string, string>>();
  const dateSet = new Set<string>();
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const processedData: Record<string, string>[] = [];
  const leftParameters = new Set<string>(historyDataParameters.vitals);

  vitalSigns.forEach(
    ({ displayName, timestamp, displayValue, displayUnit }) => {
      const dateTime = new Date(timestamp).toISOString().slice(0, 16);
      const onlyDate = new Date(timestamp).toISOString().slice(0, 10);
      if (!dateSet.has(onlyDate)) {
        dateSet.add(onlyDate);
      }

      const lowerCasedDisplayName = displayName?.toLowerCase();
      const foundParameter = historyDataParameters.vitals.find((parameter) => {
        const lowerCasedPrameter = parameter.toLowerCase();
        return (
          lowerCasedPrameter.includes(lowerCasedDisplayName) ||
          lowerCasedDisplayName?.includes(lowerCasedPrameter)
        );
      });

      // replacing display name with the paramter name
      displayName = foundParameter ?? displayName;
      if (foundParameter) {
        leftParameters.delete(foundParameter);

        if (!displayNameToVitalsMap.has(displayName)) {
          displayNameToVitalsMap.set(displayName, new Map<string, string>());
        }

        const innerMap = displayNameToVitalsMap.get(displayName);

        if (innerMap) {
          innerMap.set(dateTime, displayValue + ' ' + displayUnit);
          displayNameToVitalsMap.set(displayName, innerMap);
        }
      }
    }
  );

  if (leftParameters.size) {
    leftParameters.forEach((paramater) => {
      displayNameToVitalsMap.set(paramater, new Map<string, string>());
    });
  }

  const sortedDates = [...dateSet].sort((a, b) => new Date(a).getTime() - new Date(b).getTime()).reverse();
  callbackFn(sortedDates);
  displayNameToVitalsMap.forEach((dateToVitalMap, displayName) => {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const dateWiseReading: Record<string, any> = { displayName };

    dateToVitalMap.forEach((key, date) => {
      const onlyDate = date.slice(0, 10);
      if (!dateWiseReading[onlyDate]) {
        dateWiseReading[onlyDate] = []
      }
      dateWiseReading[onlyDate].push({
        time: date.slice(11, 16),
        value: key ?? ''
      })
    })
    processedData.push(dateWiseReading);
  });

  processedData.sort((a, b) => a['displayName'].localeCompare(b['displayName']));
  return processedData;
}

function stringToArrayBuffer(rtfString: string) {
  const buffer = new ArrayBuffer(rtfString.length);
  const bufferView = new Uint8Array(buffer);
  for (let i = 0; i < rtfString.length; i++) {
    bufferView[i] = rtfString.charCodeAt(i);
  }
  return buffer;
}

export function convertRtfToHtml(rtf: string): Promise<HTMLElement[]> {
  RTFJS.loggingEnabled(false);
  WMFJS.loggingEnabled(false);
  EMFJS.loggingEnabled(false);
  const doc = new RTFJS.Document(stringToArrayBuffer(rtf), {});
  doc.metadata();
  return doc.render();
}

export function mergeVitalTableData(
  existingData: Record<string, string>[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  newData: Record<string, any>[]
): Record<string, string>[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataMap = buildDataMap(existingData);

  for (const newRow of newData) {
    const displayName = newRow['displayName'];
    const existingRow = dataMap.get(displayName);

    if (existingRow) {
      mergeRowData(existingRow, newRow);
    } else {
      dataMap.set(displayName, { ...newRow });
    }
  }

  return Array.from(dataMap.values()).sort((a, b) =>
    a['displayName'].localeCompare(b['displayName'])
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildDataMap(data: Record<string, any>[]): Map<string, Record<string, any>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map = new Map<string, Record<string, any>>();
  for (const row of data) {
    map.set(row['displayName'], { ...row });
  }
  return map;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mergeRowData(target: Record<string, any>, source: Record<string, any>): void {
  for (const key in source) {
    if (key === 'displayName') continue;
    mergeDateEntries(target, source, key);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mergeDateEntries(target: Record<string, any>, source: Record<string, any>, key: string): void {
  if (!target[key]) {
    target[key] = [];
  }

  if (Array.isArray(target[key]) && Array.isArray(source[key])) {
    const existingTimes = new Set(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      target[key].map((item: any) => item.time)
    );

    for (const entry of source[key]) {
      if (!existingTimes.has(entry.time)) {
        target[key].push(entry);
      }
    }
  }
}